// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, MenuItem, Select, TextField, useFormControl } from '@mui/material';
import { validateIdentifier, validateFloatList } from '../../../components/EquipementsWizard/steps/InputUtils';
import { WizardUtils } from '../../../utils/WizardUtils';
import { AutocompleteField } from './AutocompleteField';
import MultiInputField from './MultiInputField';
import NestedSelect from './NestedSelect';

export const TableCellComponent = ({
  col,
  currentRow = {},
  handleChange,
  isCellDisabled,
  isCellValid,
  selectedAssetClassAttrsList,
  referentielConsequences = [],
}) => {
  const { disabled } = useFormControl();
  const inputType = useMemo(() => {
    return typeof col.inputType === 'function' ? col.inputType(currentRow) : col.inputType;
  }, [col, currentRow]);

  useEffect(() => {
    if (typeof col.disabled === 'function' && disabled) {
      if (col.clearIfDisabled) handleChange(col, '');
      else if (col.defaultValue) handleChange(col, col.defaultValue);
    }
  }, [col, disabled, handleChange]);

  const handleAttributeChange = (e) => {
    handleChange(col, e.target.value);
  };
  const getHelperText = () => {
    const val = currentRow?.[col.field];
    const isEmpty = val == null || (typeof val === 'string' && val.trim() === '');

    if (col.required && isEmpty) {
      return 'Ne doit pas être vide';
    }

    if (isCellValid && isCellValid(col)) {
      return col.errorMessage || 'Valeur invalide';
    }

    return null;
  };

  return inputType === 'number' ? (
    <TextField
      id={col.field}
      value={currentRow[col.field] ?? ''}
      type="number"
      error={!!getHelperText()}
      helperText={getHelperText()}
      InputProps={{
        inputProps: {
          step: col.step ?? 'any',
          min: col.min,
          max: col.max,
        },
      }}
      onChange={(e) => {
        const value = e.target.value;
        if (col.step === 1) {
          const parsedValue = parseInt(value);
          const finalValue = isNaN(parsedValue) ? '' : parsedValue;
          handleChange(col, finalValue);
        } else {
          handleChange(col, value);
        }
      }}
      fullWidth
      disabled={
        typeof col.disabled === 'function'
          ? col.disabled(currentRow)
          : isCellDisabled
            ? isCellDisabled(col)
            : col.disabled
      }
    />
  ) : inputType === 'select' ? (
    col.field === 'ValeurReference' ? (
      <MultiInputField
        value={currentRow[col.field]}
        onChange={(value) => handleChange(col, value)}
        options={col.options}
        selectedAssetClassAttrsList={selectedAssetClassAttrsList}
        attributDeclencheur={currentRow.AttributDeclencheur}
        disabled={isCellDisabled(col)}
        label={col.label}
      />
    ) : (
      <Select
        value={currentRow[col.field]}
        displayEmpty={true}
        data-testid={col.label}
        onChange={(e) => handleChange(col, e.target.value)}
        fullWidth
        disabled={
          typeof col.disabled === 'function'
            ? col.disabled(currentRow)
            : isCellDisabled
              ? isCellDisabled(col)
              : col.disabled
        }
      >
        {(typeof col.options === 'function' ? col.options(currentRow) : col.options)?.map((option) => (
          <MenuItem
            key={option.value ?? option}
            data-cy={option.value || option.label || option}
            value={option.value ?? option}
          >
            {option.label ?? option}
          </MenuItem>
        ))}
      </Select>
    )
  ) : inputType === 'nestedSelect' ? (
    <NestedSelect
      value={currentRow[col.field]}
      onChange={handleAttributeChange}
      options={col.options}
      disabled={isCellDisabled(col)}
      fullWidth={true}
      testId={col.label}
      selectedAssetClassAttrsList={selectedAssetClassAttrsList}
      referentielConsequences={referentielConsequences}
    />
  ) : inputType === 'multiSelect' ? (
    <Select
      value={currentRow[col.field] ? currentRow[col.field].split('|') : []}
      renderValue={(selected) => selected?.join('|')}
      fullWidth
      multiple
      data-testid={col.label}
      onChange={(e) => handleChange(col, e.target.value?.join('|'))}
      disabled={
        typeof col.disabled === 'function'
          ? col.disabled(currentRow)
          : isCellDisabled
            ? isCellDisabled(col)
            : col.disabled
      }
      sx={{ maxWidth: WizardUtils.getMaxCharWidth(col.length) }}
    >
      {(() => {
        const options = typeof col.options === 'function' ? col.options(currentRow) : col.options || [];
        return [
          ...options,
          ...(currentRow[col.field]
            ? currentRow[col.field]
                .split('|')
                .filter((value) => !options.some((opt) => (opt?.value ?? opt) === value))
                .map((value) => ({ value, label: value }))
            : []), // .map((value) => ({ value, label: `${value} (supprimée)` }))
        ].map((option) => (
          <MenuItem data-cy={option?.value ?? option} key={option?.value ?? option} value={option?.value ?? option}>
            <Checkbox checked={!!currentRow[col.field]?.split('|')?.includes(option?.value ?? option)} />
            {option.label ?? option}
          </MenuItem>
        ));
      })()}
    </Select>
  ) : inputType === 'autocomplete' ? (
    <AutocompleteField
      value={currentRow[col.field]}
      onChange={(newValue) => handleChange(col, newValue)}
      col={col}
      currentRow={currentRow}
      disabled={isCellDisabled(col)}
      id={col.label}
    />
  ) : inputType === 'buttonWithMultiSelect' ? (
    <Select
      value={currentRow[col.field] ? currentRow[col.field].split('|') : []}
      renderValue={(selected) => selected?.join('|')}
      fullWidth
      multiple
      data-testid={col.label}
      onChange={(e) => handleChange(col, e.target.value?.join('|'))}
      disabled={
        typeof col.disabled === 'function'
          ? col.disabled(currentRow)
          : isCellDisabled
            ? isCellDisabled(col)
            : col.disabled
      }
      sx={{ maxWidth: WizardUtils.getMaxCharWidth(col.length) }}
    >
      {(() => {
        const options = typeof col.options === 'function' ? col.options(currentRow) : col.options || [];
        return [
          ...options,
          ...(currentRow[col.field]
            ? currentRow[col.field]
                .split('|')
                .filter((value) => !options.some((opt) => (opt?.value ?? opt) === value))
                .map((value) => ({ value, label: value }))
            : []), // .map((value) => ({ value, label: `${value} (supprimée)` }))
        ].map((option) => (
          <MenuItem data-cy={option?.value ?? option} key={option?.value ?? option} value={option?.value ?? option}>
            <Checkbox checked={!!currentRow[col.field]?.split('|')?.includes(option?.value ?? option)} />
            {option.label ?? option}
          </MenuItem>
        ));
      })()}
    </Select>
  ) : inputType === 'listOfFloat' ? (
    <TextField
      id={col.field}
      value={currentRow[col.field] ?? ''}
      error={!!getHelperText()}
      helperText={getHelperText()}
      onChange={(e) => {
        const value = e.target.value;
        if (validateFloatList(value)) {
          handleChange(col, value);
        }
      }}
      fullWidth
      disabled={isCellDisabled(col)}
    />
  ) : (
    <TextField
      id={col.field}
      value={currentRow[col.field]}
      onChange={(e) => {
        const newValue = e.target.value;
        const isValid = validateIdentifier(newValue);
        if (isValid) {
          handleChange(col, newValue);
        }
      }}
      fullWidth
      disabled={isCellDisabled(col)}
      error={!!getHelperText()}
      helperText={getHelperText()}
    />
  );
};

TableCellComponent.propTypes = {
  col: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  currentRow: PropTypes.object,
  isCellDisabled: PropTypes.func,
  isCellValid: PropTypes.func,
  selectedAssetClassAttrsList: PropTypes.array,
  referentielConsequences: PropTypes.array,
};
